import { FunctionComponent, useState, useEffect, useContext, useCallback } from "react";
import { useHistory, useLocation } from "react-router";
import { Dropdown } from "components/dropdown";
import { UserContext } from "components/user";
import { PageLayout, Wrapper } from "components/layout";
import { UserNeedsAuthentication } from "components/user";
import { Text } from "components/text";
import useCartaObjectStorage from "hooks/useCartaObjectStorage";
import {  IProjectProps } from "data/types";
import { FavoritesPanel } from "components/favoritesPanel";
import { useProject } from "hooks/useProject";

export interface IFormProps {
  title?: string;
  key: string;
}

const CollectionAuthenticated: FunctionComponent = () => {
  const [error, setError] = useState<string>();
  const [projects, setProjects] = useState<IProjectProps[]>([]);
  const [projectId, setProjectId] = useState<string>();

  const location = useLocation();
  const history = useHistory();
  const cartaObjectStorage = useCartaObjectStorage();
  const projectCache = useProject();

  const projectIdFromUrl = useCallback(() => new URLSearchParams(location.search).get("projectId"), [location]);

  useEffect(() => {
    (async () => {
      try {
        if(!cartaObjectStorage){
          return;
        }

        const projectList = projectCache.getProjects();
        if (!projectList) {
          // Not loaded yet; this effect re-runs once ProjectCacheProvider's
          // startup fetch populates it.
          return;
        }

        setProjects(projectList);
        const _projectIdFromUrl = projectIdFromUrl();
        if (_projectIdFromUrl) {
          setProjectId(_projectIdFromUrl);
        }

        if (projectList.length === 1) setProjectId(projectList[0].projectId);
      } catch (error) {
        if (error instanceof Error) setError(error.message);
        else setError("An unknown error occurred.");
      }
    })();
  }, [cartaObjectStorage, projectIdFromUrl, projectCache]);

  // Old links used ?projectId=<id> query params; redirect those to the new
  // path-based /<projectId> route (Project.tsx) so they keep working.
  useEffect(() => {
    if (projectId) {
      history.replace(`/${projectId}`);
    }
  }, [projectId, history]);

  return (
    <div style={{ display: "flex", flex: 1 }}>
      {!projectId && projects.length > 1 && (
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", flex: 1 }}>
          <div style={{ flex: 1, flexBasis: "50%" }}>
            <Dropdown side="bottom-left">
              <Dropdown.Toggler caret>
                <Text size="medium">Select Project</Text>
              </Dropdown.Toggler>
              <Dropdown.Area>
                {projects.map((item: IProjectProps) => (
                  <Dropdown.Item key={item.projectId} href={`/${item.projectId}`}>
                    {item.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Area>
            </Dropdown>
            <Text color="error" size="small" padding="center">
              {error}
            </Text>
          </div>
          <div style={{ flex: 1, flexBasis: "50%" }}>
            <FavoritesPanel />
          </div>
        </div>
      )}
    </div>
  );
};

const CollectionPage: FunctionComponent = () => {
  const { authenticated } = useContext(UserContext);

  return (
    <PageLayout header footer>
      <Wrapper>
        {authenticated ? (
          // By adding the key property, when the page is refreshed,
          // the state will be reset and all data will be fetched from scratch
          <CollectionAuthenticated key={Date.now()} />
        ) : (
          <UserNeedsAuthentication />
        )}
      </Wrapper>
    </PageLayout>
  );
};

export default CollectionPage;
